import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { HoldTypeSwatch } from '../../../components/climbs/climb-editor/HoldTypeSwatch';
import { holdTypesService } from '../../../services/hold-types';
import type { HoldType } from '../../../types/hold-type';

/** Radix Select reads '' as "no value", so "leave them without one" needs a token. */
const NO_TARGET = '__none__';

/**
 * How many other types the climbs may be sent to. Read here rather than taken
 * from the panel because that only holds the page you are looking at, and the
 * type these climbs belong on is as likely to be on another one.
 */
const CANDIDATES = 100;

interface HoldTypeDeleteDialogProps {
  /** The type being retired, or null when the dialog is closed. */
  type: HoldType | null;
  locationId: string;
  deleting: boolean;
  onCancel: () => void;
  onConfirm: (reassignTo: string | null) => void;
}

/**
 * Where a retired hold type's climbs go.
 *
 * Deleting one is nearly always a tidy-up rather than a loss — two spellings of
 * the same colour being merged, or a wall reset from "tacche arancioni" onto the
 * mixed row — so the climbs are offered a new home first. Leaving them without
 * one is still there, second, for the case where the type was simply wrong.
 *
 * A type nothing is set in skips the question: there is nothing to move.
 */
export function HoldTypeDeleteDialog({
  type,
  locationId,
  deleting,
  onCancel,
  onConfirm,
}: HoldTypeDeleteDialogProps) {
  const { t } = useTranslation();
  const [target, setTarget] = useState(NO_TARGET);
  const [candidates, setCandidates] = useState<HoldType[]>([]);

  // Each deletion is its own question; the last answer must not carry over.
  useEffect(() => setTarget(NO_TARGET), [type?.id]);

  // Only worth asking for when there are climbs to move.
  const id = type?.id;
  const inUse = (type?.climbs_count ?? 0) > 0;
  useEffect(() => {
    if (!id || !inUse) {
      setCandidates([]);
      return;
    }
    let cancelled = false;
    holdTypesService
      .list(locationId, { perPage: CANDIDATES })
      .then(({ types }) => {
        if (!cancelled) setCandidates(types.filter((row) => row.id !== id));
      })
      .catch(() => {
        if (!cancelled) setCandidates([]);
      });
    return () => {
      cancelled = true;
    };
  }, [locationId, id, inUse]);

  if (!type) return null;

  const count = type.climbs_count ?? 0;
  const canMove = count > 0 && candidates.length > 0;

  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('gymAdmin.holdTypes.deleteDialog.title', { name: type.name })}</DialogTitle>
          <DialogDescription>
            {count > 0
              ? t('gymAdmin.holdTypes.deleteDialog.inUse', { name: type.name, count })
              : t('gymAdmin.holdTypes.deleteDialog.unused', { name: type.name })}
          </DialogDescription>
        </DialogHeader>

        {canMove && (
          <div className="space-y-2">
            <Label htmlFor="hold-type-reassign">
              {t('gymAdmin.holdTypes.deleteDialog.moveTo')}
            </Label>
            <Select value={target} onValueChange={setTarget}>
              <SelectTrigger id="hold-type-reassign">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_TARGET}>
                  {t('gymAdmin.holdTypes.deleteDialog.leaveWithout')}
                </SelectItem>
                {candidates.map((row) => (
                  <SelectItem key={row.id} value={row.id}>
                    <span className="flex items-center gap-2">
                      <HoldTypeSwatch type={row} />
                      {row.name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={deleting}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="destructive"
            disabled={deleting}
            onClick={() => onConfirm(target === NO_TARGET ? null : target)}
          >
            {deleting && <Loader2 className="mr-2 size-4 animate-spin" />}
            {t('gymAdmin.holdTypes.deleteDialog.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
